import { isBadRequestStatus, isUpstreamHttpError } from './upstream-http-error';

type JsonLike =
  | string
  | number
  | boolean
  | null
  | JsonLike[]
  | { [key: string]: JsonLike };

type LogLevel = 'info' | 'warn' | 'error';

type LogError = {
  name: string;
  message: string;
  stack?: string | null;
  status?: number | null;
  upstream?: string | null;
  operation?: string | null;
  retryable?: boolean | null;
  cause?: JsonLike;
};

export interface ServerLogEvent {
  event: string;
  level: LogLevel;
  service?: string | null;
  route?: string | null;
  upstream?: string | null;
  status?: number | null;
  request_id?: string | null;
  is_bot?: boolean | null;
  country?: string | null;
  seo_id?: string | number | null;
  sample_rate?: number | null;
  count_estimate?: number | null;
  timestamp?: string;
  message?: string;
  context?: Record<string, unknown> | null;
  error?: unknown;
  sampling_key?: string;
}

export interface ServerLogResult {
  logged: boolean;
  payload: Record<string, unknown>;
}

const REDACTED = '[REDACTED]';
const MAX_STRING_LENGTH = 1000;
const MAX_DEPTH = 6;
const MAX_ARRAY_ITEMS = 50;
const MAX_OBJECT_KEYS = 100;

const SENSITIVE_KEY =
  /access[_-]?token|authorization|api[_-]?key|secret|password|cookie|set-cookie|token/i;
const SENSITIVE_QUERY_PARAM =
  /^(access_token|token|api[_-]?key|key|authorization|auth|password|secret)$/i;
const BEARER_PATTERN = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi;

function clampSampleRate(rate?: number | null): number {
  if (typeof rate !== 'number' || Number.isNaN(rate)) return 1;
  if (rate <= 0) return 0;
  if (rate > 1) return 1;
  return rate;
}

function getDeterministicRatio(key: string): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) / 4294967295;
}

function shouldSampleLog(key: string, sampleRate: number): boolean {
  if (sampleRate >= 1) return true;
  if (sampleRate <= 0) return false;
  return getDeterministicRatio(key) < sampleRate;
}

function sanitizeUrl(value: string): string {
  if (!value.includes('://') && !value.includes('access_token=')) {
    return value;
  }

  try {
    const url = new URL(value);
    url.searchParams.forEach((_, queryKey) => {
      if (SENSITIVE_QUERY_PARAM.test(queryKey)) {
        url.searchParams.set(queryKey, REDACTED);
      }
    });
    return url.toString().replace(/%5BREDACTED%5D/gi, REDACTED);
  } catch {
    return value.replace(
      /([?&](?:access_token|token|api[_-]?key|authorization|auth|password|secret)=)([^&]+)/gi,
      `$1${REDACTED}`
    );
  }
}

function sanitizeString(value: string): string {
  const redactedBearer = value.replace(BEARER_PATTERN, `Bearer ${REDACTED}`);
  const sanitizedUrl = sanitizeUrl(redactedBearer);
  return sanitizedUrl.length <= MAX_STRING_LENGTH
    ? sanitizedUrl
    : `${sanitizedUrl.slice(0, MAX_STRING_LENGTH)}...[truncated]`;
}

function sanitizeUnknown(
  input: unknown,
  depth = 0,
  seen: WeakSet<object> = new WeakSet<object>()
): JsonLike {
  if (input == null) return null;
  if (typeof input === 'string') return sanitizeString(input);
  if (typeof input === 'number' || typeof input === 'boolean') return input;
  if (typeof input === 'bigint') return Number(input);
  if (typeof input === 'symbol') return String(input);
  if (typeof input === 'function') return '[Function]';

  if (input instanceof Date) return input.toISOString();
  if (input instanceof URL) return sanitizeUrl(input.toString());
  if (input instanceof Error) return normalizeError(input);
  if (depth >= MAX_DEPTH) return '[MaxDepthReached]';

  if (Array.isArray(input)) {
    return input
      .slice(0, MAX_ARRAY_ITEMS)
      .map((value) => sanitizeUnknown(value, depth + 1, seen));
  }

  if (typeof input === 'object') {
    const obj = input as Record<string, unknown>;
    if (seen.has(obj)) return '[Circular]';
    seen.add(obj);

    const entries = Object.entries(obj).slice(0, MAX_OBJECT_KEYS);
    const sanitized: Record<string, JsonLike> = {};

    for (const [rawKey, rawValue] of entries) {
      const key = sanitizeString(rawKey);
      sanitized[key] = SENSITIVE_KEY.test(rawKey)
        ? REDACTED
        : sanitizeUnknown(rawValue, depth + 1, seen);
    }

    return sanitized;
  }

  return sanitizeString(String(input));
}

function normalizeError(error: unknown): LogError {
  if (isUpstreamHttpError(error)) {
    return {
      name: error.name,
      message: sanitizeString(error.message),
      stack: error.stack ? sanitizeString(error.stack) : null,
      status: error.status,
      upstream: error.upstream,
      operation: error.operation,
      retryable: error.retryable,
      cause: sanitizeUnknown(error.cause),
    };
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: sanitizeString(error.message),
      stack: error.stack ? sanitizeString(error.stack) : null,
      retryable: null,
    };
  }

  return {
    name: 'UnknownError',
    message: sanitizeString(String(error)),
    retryable: null,
  };
}

export function sanitizeLogContext(
  context: Record<string, unknown> | null | undefined
): Record<string, JsonLike> | null {
  if (!context) return null;
  const sanitized = sanitizeUnknown(context);
  return typeof sanitized === 'object' && sanitized && !Array.isArray(sanitized)
    ? (sanitized as Record<string, JsonLike>)
    : null;
}

export function buildSamplingKey(event: ServerLogEvent): string {
  return [
    event.event,
    event.upstream || 'unknown',
    event.route || 'unknown',
    typeof event.status === 'number' ? String(event.status) : 'na',
    event.country || 'na',
    event.seo_id != null ? String(event.seo_id) : 'na',
  ].join('|');
}

export function logServerEvent(event: ServerLogEvent): ServerLogResult {
  const sampleRate = clampSampleRate(event.sample_rate);
  const samplingKey = event.sampling_key || buildSamplingKey(event);
  const sampledIn = shouldSampleLog(samplingKey, sampleRate);
  const countEstimate =
    event.count_estimate ??
    (sampleRate > 0 ? Math.max(1, Math.round(1 / sampleRate)) : null);

  const payload = {
    event: event.event,
    level: event.level,
    service: event.service || 'bx-capterra',
    route: event.route || null,
    upstream: event.upstream || null,
    status: event.status ?? null,
    request_id: event.request_id || null,
    is_bot: event.is_bot ?? null,
    country: event.country || null,
    seo_id:
      event.seo_id !== undefined && event.seo_id !== null
        ? String(event.seo_id)
        : null,
    sample_rate: sampleRate,
    count_estimate: countEstimate,
    timestamp: event.timestamp || new Date().toISOString(),
    message: event.message ? sanitizeString(event.message) : null,
    error: event.error ? normalizeError(event.error) : null,
    context: sanitizeLogContext(event.context),
  };

  if (!sampledIn) {
    return { logged: false, payload };
  }

  // biome-ignore lint/suspicious/noConsole: Centralized server logger output sink.
  if (event.level === 'error') console.error(JSON.stringify(payload));
  // biome-ignore lint/suspicious/noConsole: Centralized server logger output sink.
  else if (event.level === 'warn') console.warn(JSON.stringify(payload));
  // biome-ignore lint/suspicious/noConsole: Centralized server logger output sink.
  else console.info(JSON.stringify(payload));

  return { logged: true, payload };
}

export function getLogLevelForUpstreamStatus(status?: number | null): LogLevel {
  if (typeof status !== 'number') {
    return 'info';
  }

  if (isBadRequestStatus(status)) {
    return 'warn';
  }

  // Treat common transient upstream statuses as sampled warnings.
  if (status === 429 || status === 502 || status === 503 || status === 504) {
    return 'warn';
  }

  return status >= 500 ? 'error' : 'info';
}
